import React, { useEffect } from "react";
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation, } from 'react-i18next';
import ProductSearch from "../../Components/Order/ProductSearch";
import CustomerPurchaseHistory from "../../Components/OrderPlacement/CustomerPurchaseHistory";
import VbProductContainer from "./VbProductContainer";
import BlikkProductContainer from "./BlikkProductContainer";
import orderActions from '../../Actions/orderActions';
import commonActions from '../../Actions/commonActions';
import system from '../../Constants/system';
import Grid from "../../Components/Grid/Grid";
import OrderCartContainer from "./OrderCartContainer";
import MessageAlertLevel from "../../Components/MessageAlertLevel";
import { hasReadWriteAccess } from "../../Utils/permissionUtils";
import { UserRoles } from "../../Constants/userRoles";
import VossProductContainer from "./VossProductContainer";
import { ProductConfirmation } from "../../Components/OrderPlacement/ProductComponents/ProductConfirmation";
import { ProductWarnings } from "../../Components/OrderPlacement/ProductComponents/ProductWarnings";
import customerActions from "../../Actions/customerActions";
import targetGroupHelpers from "../../Utils/targetGroups";
import { OrderLimitationReasons, BANKRUPTCY_TRUSTEE_PRODUCT_ID } from "../../Constants/bankruptcyConstants";
import { useProductSearch } from "../../Hooks/useProductSearch";

const OrderPlacementContainer = ({ ...props }) => {

  const { orderActions, customerActions, orderId, order, vbProducts, subscriptions, customer, pushedQuotation, user, product, sourceSystem, orderLimitationReasons, productDetailsLoading, productCompatibilityRules, editOrderline } = props;
  const { leadId } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams()
  const packageIdParam = searchParams.get("packageId")
  const [packageId, setPackageId] = React.useState(null)
  const { search: onSearch, results: searchResults } = useProductSearch(order?.customerNumber, order?.customerId);

  useEffect(() => {
    if (!orderLimitationReasons && customer.vbCustomer)
      customerActions.getOrderLimitationReasons(customer.vbCustomer?.customerNumber);

    orderActions.startOrder(customer.vbCustomer?.customerNumber, customer.vossCustomer?.id, leadId, pushedQuotation);

    if (packageIdParam)
      setPackageId(packageIdParam);

    getCustomerProducts();

    if (!productCompatibilityRules) {
      orderActions.getProductCompatibilityRules();
    }

    return () => {
      orderActions.clearOrder();
      orderActions.clearPushedQuotation();
    };
  }, []);

  useEffect(() => {
    const isBankruptcy = orderLimitationReasons?.includes(OrderLimitationReasons.Bankruptcy);

    if (isBankruptcy && order && customer.vossCustomer && !product) {
      orderActions.getVossProductDetails(
        BANKRUPTCY_TRUSTEE_PRODUCT_ID,
        order.orderId
      );
    }
  }, [orderLimitationReasons, order, customer.vossCustomer, product, orderActions]);

  useEffect(() => {
    if (order && packageId) {
      orderActions.getVossProductDetails(packageId, order.orderId);
      setPackageId(null);
    }
  }, [order, packageId]);

  const getCustomerProducts = () => {
    if (customer.vbCustomer && !vbProducts) {
      customerActions.getCustomerVBProducts(customer.vbCustomer.customerNumber);
    }
    if (customer.vossCustomer && !subscriptions) {
      customerActions.searchSubscriptions(customer.vossCustomer.id);
    }
  }

  const onProductClick = (productId, sourceSystem) => {
    switch (sourceSystem) {
      case system.Vb:
        orderActions.getProductDetails(productId, customer.vbCustomer.customerNumber, 0, null, null, orderId);
        break;
      case system.Blikk:
        orderActions.getBlikkProductDetails(productId, customer.vbCustomer.customerNumber, orderId);
        break;
      case system.Voss:
        orderActions.getVossProductDetails(productId, orderId);
        break;
      default:
        break;
    }
  };

  const parseValidationRules = (orderLimitationReason) => {
    if (targetGroupHelpers.isNonInteractive(customer))
      return "ordercontainer.noninteractive"

    const reason = orderLimitationReason?.length > 0 && orderLimitationReason[0];

    if (reason === OrderLimitationReasons.Bankruptcy) {
      return null;
    }

    switch (reason) {
      case OrderLimitationReasons.BadGroupType:
        return 'ordercontainer.badgrouptype'
      case OrderLimitationReasons.BadStatus:
        return 'ordercontainer.badstatus'
      case OrderLimitationReasons.AOProduct:
        if (user && hasReadWriteAccess(user, UserRoles.AOSales))
          break;
        return 'ordercontainer.aoproduct'
      case OrderLimitationReasons.AOCentralAgreement:
        if (user && hasReadWriteAccess(user, UserRoles.AOSales))
          break;
        return 'ordercontainer.aocentralagreement'
      case OrderLimitationReasons.NoMainCustomer:
        return 'ordercontainer.nomaincustomer'
      default:
        return null;
    }
  }

  const message = parseValidationRules(orderLimitationReasons);
  if (message) return <MessageAlertLevel message={t(message)} />;

  const isBankruptcy = orderLimitationReasons?.includes(OrderLimitationReasons.Bankruptcy);

  return <Grid>
    {!isBankruptcy && (
      <div className="mt-24">
        <ProductSearch title={t('ordercontainer.productsearch')} results={searchResults} onSearch={onSearch} onClear={() => onSearch('')} onProductClick={onProductClick} isVonEnabled={customer.vonCustomer !== null || customer.vossCustomer !== null} />
      </div>
    )}
    {isBankruptcy && (
      <div className="mt-24">
        <MessageAlertLevel message={t('ordercontainer.bankruptcyrestriction')} level="warning" />
      </div>
    )}
    <div>
      <div className="mb-24">
        <h2 className="font-family-ubuntu-semibold mb-0">{product?.productDescription}</h2>
      </div>
      <div className="mb-64">
        <Grid>
          <Grid.Row>
            {productDetailsLoading ? <div className="spinner spinner-default-green m-auto d-block" /> : <Grid.Row.Column>
              {product && order && <>
                <ProductConfirmation {...props} />
                <ProductWarnings {...props} />
                {sourceSystem === system.Vb && <VbProductContainer {...props} />}
                {sourceSystem === system.Blikk && <BlikkProductContainer {...props} />}
                {sourceSystem === system.Voss && <VossProductContainer order={order} product={product} editOrderline={editOrderline} />}
              </>}
            </Grid.Row.Column>}
            <Grid.Row.Column width={3}>
              <CustomerPurchaseHistory onProductClick={(productId, sourceSystem) => onProductClick(productId, sourceSystem)} {...props} />
            </Grid.Row.Column>
          </Grid.Row>
        </Grid>
      </div>
      {order && order.orderlines.length > 0 && <OrderCartContainer {...props} product={product} />}
    </div>
  </Grid>
};

const mapStateToProps = (state) => ({
  orderId: state.OrderReducer.orderId,
  order: state.OrderReducer.order,
  customer: state.CustomerReducer.customer,
  vbProducts: state.CustomerReducer.vbProducts,
  productDetailsLoading: state.CommonReducer.dataLoading.productDetails,
  productsLoading: state.CommonReducer.dataLoading.vbProducts || state.CommonReducer.dataLoading.subscriptions,
  subscriptions: state.CustomerReducer.subscriptions,
  orderForm: state.CommonReducer.forms && state.CommonReducer.forms.orderForm,
  pushedQuotation: state.OrderReducer.pushedQuotation,
  user: state.CommonReducer.user,
  product: state.OrderReducer.product,
  sourceSystem: state.OrderReducer.sourceSystem,
  editOrderline: state.OrderReducer.editOrderline,
  orderLimitationReasons: state.CustomerReducer.orderLimitationReasons,
  productCompatibilityRules: state.OrderReducer.productCompatibilityRules
});

const mapDispatchToProps = (dispatch) => ({
  orderActions: bindActionCreators(orderActions, dispatch),
  commonActions: bindActionCreators(commonActions, dispatch),
  customerActions: bindActionCreators(customerActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderPlacementContainer);